import { CATEGORY_ADMIN_ROLES } from '@/components/auth/category-admin-roles';
import { RequireDashboardRole } from '@/components/auth/RequireDashboardRole';
import { breadcrumbTracker } from '@/components/helpdesk';
import { AppLayout } from '@/components/layout';
import { WIZARD_ROUTES } from '@/components/layout/nav-config';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import {
  AttendancePage,
  EmployeeDetailPage,
  EmployeeListPage,
  LeaveCalendarPage,
  LeaveRequestPage,
} from '@/features/hr/pages';
import {
  PayrollConfigPage,
  PayrollDashboardPage,
  PayrollRunDetailPage,
  PayrollRunListPage,
  PayrollRunNewPage,
  PayslipPage,
} from '@/features/payroll/pages';
import { CRITICAL_OFFLINE_ENDPOINTS, prefetchForOffline } from '@/lib/offline-api-layer';
import { defaultQueryRetry } from '@/lib/query-retry';
import {
  AdminCenterPage,
  BankImportListPage,
  BomEditorPage,
  BomListPage,
  BulkEmailPage,
  BusinessHoursPage,
  CategoryHourlyRateConfigPage,
  ChatPage,
  CompanyVehicleDetailPage,
  CompanyVehicleFormPage,
  CompanyVehicleListPage,
  ContractDetailPage,
  ContractFormPage,
  ContractListPage,
  CorrectionFormPage,
  CourierDeliveryDetailPage,
  CourierDeliveryListPage,
  DamageClaimDetailPage,
  DamageClaimListPage,
  DashboardPage,
  DeliveryNoteDetailPage,
  DeliveryNoteFormPage,
  DeliveryNoteListPage,
  DocumentsAuditPage,
  DuplicatesPage,
  EmailLogPage,
  EmailSmtpSettingsPage,
  EmailTemplateListPage,
  ExpenseVoucherPage,
  ExpiringDocumentsPage,
  FeatureFlagsPage,
  FixedAssetDetailPage,
  FixedAssetListPage,
  FleetCalendarPage,
  FlowPage,
  GoodsReceiptDetailPage,
  GoodsReceiptFormPage,
  GoodsReceiptListPage,
  InboundSmsPage,
  InboxListPage,
  InboxThreadDetailPage,
  InventoryAnalyticsPage,
  InventoryDetailPage,
  InventoryListPage,
  InventoryMovementsPage,
  InventoryStockSummaryPage,
  InventoryTransferPage,
  InvoiceDetailPage,
  InvoiceFormPage,
  InvoiceListPage,
  JournalPage,
  KasszaPage,
  KbAdminPage,
  KbSearchPage,
  LoginPage,
  LoyaltyPage,
  MachineCategoryAdminPage,
  MonthlyClosePage,
  MyForgeOSPage,
  NotificationPreferencesPage,
  OcrSettingsPage,
  OverdueRentalsPage,
  PackageAdminPage,
  PackageEditorPage,
  PackageListPage,
  PartnerCreatePage,
  PartnerDetailPage,
  PartnerEditPage,
  PartnerListPage,
  PartnerTimelinePage,
  PriLlmSettingsPage,
  ProcurementCartPage,
  ProductCreatePage,
  ProductDetailPage,
  ProductEditPage,
  ProductImportPage,
  ProductionOrderDetailPage,
  ProductionOrderListPage,
  ProductListPage,
  PublicInquiryPage,
  PublicServiceRequestPage,
  QuotationDetailPage,
  QuotationFormPage,
  QuotationListPage,
  ReceivablesPage,
  ReleaseHubPage,
  RentalDetailPage,
  RentalListPage,
  RentalReturnPage,
  RentalVehicleDetailPage,
  RentalVehicleFormPage,
  RentalVehicleListPage,
  RentalWizardPage,
  ReportsPage,
  RoleManagementPage,
  SalesDetailPage,
  SalesListPage,
  SalesPOSPage,
  ScrapListPage,
  ScrapProtocolPage,
  ServiceAfterlifePage,
  SettingsAutoReplyPage,
  SettingsChannelsPage,
  SettingsPage,
  SmsDashboardPage,
  SmsSettingsPage,
  StockCountListPage,
  SupplierSettlementPage,
  SzhMappaPage,
  TamDeclarationsPage,
  TasksPage,
  TenantSettingsPage,
  TwentyCRMPage,
  UserDetailPage,
  UserFormPage,
  UserListPage,
  VakzarasPage,
  WidgetPermissionsPage,
  WorksheetBatchIntakePage,
  WorksheetDetailPage,
  WorksheetEditPage,
  WorksheetListPage,
  WorksheetWizardPage,
} from '@/pages';
import { EquipmentTypeEditorPage } from '@/pages/equipment-types/EquipmentTypeEditorPage';
import { EquipmentTypesListPage } from '@/pages/equipment-types/EquipmentTypesListPage';
import { EquipmentUnitEditorPage } from '@/pages/equipment/EquipmentUnitEditorPage';
import { useAuthStore } from '@/stores/auth-store';
import { InstallPrompt, Toaster, UpdatePrompt, useServiceWorker } from '@kgc/ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ComponentType } from 'react';
import { lazy, Suspense, useEffect, useRef } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';

// Dev-only page — lazy-loaded so prod bundle never includes it
// The conditional route registration below further ensures tree-shaking
const DevTenantSwitcherPageLazy = import.meta.env.DEV
  ? lazy(() =>
      import('@/pages/dev/TenantSwitcherPage').then(m => ({ default: m.TenantSwitcherPage }))
    )
  : // Prod fallback: never rendered (route not registered in prod), but satisfies TS
    (null as unknown as ComponentType);

// E-105 Phase 2 package UI live preview — dev-only vizuális review
const DevPackagePreviewPageLazy = import.meta.env.DEV
  ? lazy(() =>
      import('@/pages/dev/PackagePreviewPage').then(m => ({ default: m.PackagePreviewPage }))
    )
  : (null as unknown as ComponentType);

/** Helpdesk breadcrumb tracking — route változásokat rögzíti */
function BreadcrumbTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    breadcrumbTracker.push(pathname);
  }, [pathname]);
  return null;
}

/** InstallPrompt wizard route-okon nem jelenik meg (WizardShell footer konfliktuselkerülés) */
function WizardAwareInstallPrompt() {
  const { pathname } = useLocation();
  const cleanPathname = pathname.split('?')[0].replace(/\/$/, '');
  if ((WIZARD_ROUTES as readonly string[]).includes(cleanPathname)) return null;
  return <InstallPrompt position="bottom" />;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      // QA-132 / Q-T351: az RQ-default `retry: 3` a `429`-et is ujraprobalja, amitol a
      // rate-limitelt hivas ~14 s-ig fuggoben marad es a `networkidle` sosem all be.
      retry: defaultQueryRetry,
    },
  },
});

function ProtectedLayout() {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  // FE `2` (kör-71): a boundary kulcsa az útvonal ⇒ navigáláskor a hiba-állapot feloldódik.
  const { pathname } = useLocation();

  // Fix #8: Prefetch critical data once after login (guard prevents double-fetch)
  // Fix CR-1: Pass auth headers so prefetch requests are not rejected with 401
  const hasPrefetched = useRef(false);
  useEffect(() => {
    if (isAuthenticated && !hasPrefetched.current) {
      hasPrefetched.current = true;
      const { accessToken, user } = useAuthStore.getState();
      const headers: Record<string, string> = {};
      if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
      const tenantId = (user as { tenantId?: string } | null)?.tenantId;
      if (tenantId) headers['X-Tenant-ID'] = tenantId;
      void prefetchForOffline([...CRITICAL_OFFLINE_ENDPOINTS], headers);
    }
    if (!isAuthenticated) {
      hasPrefetched.current = false;
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AppLayout>
      <RouteErrorBoundary key={pathname}>
        <Outlet />
      </RouteErrorBoundary>
    </AppLayout>
  );
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function App() {
  const { updateAvailable, skipWaiting } = useServiceWorker({
    path: '/sw.js',
    onUpdateAvailable: () => console.log('[SW] Új verzió elérhető'),
  });

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        {updateAvailable && <UpdatePrompt onUpdate={skipWaiting} position="top" />}
        <BreadcrumbTracker />
        <WizardAwareInstallPrompt />
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />

          {/* SRV-FG-5-4: Public service request (no auth) */}
          <Route path="/public/service-request" element={<PublicServiceRequestPage />} />

          {/* E-102 P2-4: Public inquiry form (no auth, hCaptcha protected) */}
          <Route path="/public/inquiry/:tenantSlug?" element={<PublicInquiryPage />} />

          {/* Protected routes with layout */}
          <Route element={<ProtectedLayout />}>
            <Route path="/dashboard" element={<DashboardPage />} />

            {/* Admin Center routes */}
            <Route path="/admin-center" element={<AdminCenterPage />} />
            <Route path="/admin-center/kb" element={<KbSearchPage />} />
            <Route path="/admin-center/kb/admin" element={<KbAdminPage />} />
            <Route path="/admin-center/pri-settings" element={<PriLlmSettingsPage />} />
            <Route path="/admin-center/release-hub" element={<ReleaseHubPage />} />
            <Route path="/admin-center/documents/audit" element={<DocumentsAuditPage />} />

            {/* Rental routes */}
            <Route path="/rental" element={<RentalListPage />} />
            <Route path="/rental/new" element={<RentalWizardPage />} />
            <Route path="/rental/overdue" element={<OverdueRentalsPage />} />
            <Route path="/rental/return" element={<RentalReturnPage />} />
            <Route path="/rental/fleet-calendar" element={<FleetCalendarPage />} />
            <Route path="/rental/:id" element={<RentalDetailPage />} />

            {/* Worksheet routes */}
            <Route path="/worksheet" element={<WorksheetListPage />} />
            <Route path="/worksheet/new" element={<WorksheetWizardPage />} />
            <Route path="/worksheet/batch" element={<WorksheetBatchIntakePage />} />
            <Route path="/worksheet/courier" element={<CourierDeliveryListPage />} />
            <Route path="/worksheet/courier/:id" element={<CourierDeliveryDetailPage />} />
            <Route path="/worksheet/damage-claims" element={<DamageClaimListPage />} />
            <Route path="/worksheet/damage-claims/:id" element={<DamageClaimDetailPage />} />
            <Route path="/worksheet/afterlife" element={<ServiceAfterlifePage />} />
            <Route path="/worksheet/:id" element={<WorksheetDetailPage />} />
            <Route path="/worksheet/:id/edit" element={<WorksheetEditPage />} />

            {/* Sales routes */}
            <Route path="/sales" element={<SalesListPage />} />
            <Route path="/sales/new" element={<SalesPOSPage />} />
            <Route path="/sales/:id" element={<SalesDetailPage />} />

            {/* Partner routes */}
            <Route path="/partners" element={<PartnerListPage />} />
            <Route path="/partners/new" element={<PartnerCreatePage />} />
            <Route path="/partners/loyalty" element={<LoyaltyPage />} />
            <Route path="/partners/duplicates" element={<DuplicatesPage />} />
            <Route path="/partners/:id" element={<PartnerDetailPage />} />
            <Route path="/partners/:id/edit" element={<PartnerEditPage />} />

            {/* Equipment Type catalog routes (E-123, STORY-1306) */}
            <Route path="/admin/equipment-types" element={<EquipmentTypesListPage />} />
            <Route path="/admin/equipment-types/new" element={<EquipmentTypeEditorPage />} />
            <Route path="/admin/equipment-types/:id/edit" element={<EquipmentTypeEditorPage />} />
            {/* Equipment Unit editor (egyedi gép-rekord, W2 equipment/01-unit-editor) */}
            <Route path="/equipment/:id" element={<EquipmentUnitEditorPage />} />

            {/* Goods Receipt routes (Epic 21: Bevételezés) */}
            <Route path="/goods-receipt" element={<GoodsReceiptListPage />} />
            <Route path="/goods-receipt/new" element={<GoodsReceiptFormPage />} />
            <Route path="/goods-receipt/:id" element={<GoodsReceiptDetailPage />} />

            {/* Inventory routes */}
            <Route path="/inventory" element={<InventoryListPage />} />
            <Route path="/procurement" element={<ProcurementCartPage />} />
            <Route path="/inventory/summary" element={<InventoryStockSummaryPage />} />
            <Route path="/inventory/movements" element={<InventoryMovementsPage />} />
            {/* P0-4: /inventory/receive halott lap → a működő goods-receipt flow-ra irányít (Epic 21) */}
            <Route
              path="/inventory/receive"
              element={<Navigate to="/goods-receipt/new" replace />}
            />
            <Route path="/inventory/transfer" element={<InventoryTransferPage />} />
            <Route path="/inventory/scrap" element={<ScrapListPage />} />
            <Route path="/inventory/scrap/protocols" element={<ScrapProtocolPage />} />
            <Route path="/inventory/analytics" element={<InventoryAnalyticsPage />} />
            {/* Q-T109 fázis-1: dedikált leltár-lista route — a /inventory/:id catch-all ELÉ,
                különben ":id"="stock-count" rossz hívást generálna (400) */}
            <Route path="/inventory/stock-count" element={<StockCountListPage />} />
            <Route path="/inventory/:id" element={<InventoryDetailPage />} />

            {/* Package routes */}
            <Route path="/packages" element={<PackageListPage />} />
            <Route path="/packages/new" element={<PackageEditorPage />} />
            <Route path="/packages/:id" element={<PackageEditorPage />} />
            <Route path="/packages/:id/admin" element={<PackageAdminPage />} />
            <Route path="/packages/:id/edit" element={<PackageEditorPage />} />

            {/* Manufacturing routes */}
            <Route path="/manufacturing/bom" element={<BomListPage />} />
            <Route path="/manufacturing/bom/new" element={<BomEditorPage />} />
            <Route path="/manufacturing/bom/:id" element={<BomEditorPage />} />
            <Route path="/manufacturing/orders" element={<ProductionOrderListPage />} />
            <Route path="/manufacturing/orders/new" element={<ProductionOrderListPage />} />
            <Route path="/manufacturing/orders/:id" element={<ProductionOrderDetailPage />} />

            {/* Product routes */}
            <Route path="/products" element={<ProductListPage />} />
            <Route path="/products/new" element={<ProductCreatePage />} />
            <Route path="/products/import" element={<ProductImportPage />} />
            <Route path="/products/:id" element={<ProductDetailPage />} />
            <Route path="/products/:id/edit" element={<ProductEditPage />} />

            {/* Quotation routes */}
            <Route path="/quotations" element={<QuotationListPage />} />
            <Route path="/quotations/new" element={<QuotationFormPage />} />
            <Route path="/quotations/:id" element={<QuotationDetailPage />} />
            <Route path="/quotations/:id/edit" element={<QuotationFormPage />} />

            {/* Contract routes */}
            <Route path="/contracts" element={<ContractListPage />} />
            <Route path="/contracts/:id" element={<ContractDetailPage />} />
            <Route path="/contracts/:id/edit" element={<ContractFormPage />} />

            {/* Invoice routes */}
            <Route path="/invoices" element={<InvoiceListPage />} />
            <Route path="/invoices/new" element={<InvoiceFormPage />} />
            <Route path="/invoices/correction/:id" element={<CorrectionFormPage />} />
            <Route path="/invoices/:id" element={<InvoiceDetailPage />} />
            <Route path="/invoices/:id/edit" element={<InvoiceFormPage />} />

            {/* Delivery Note routes (Epic 63) */}
            <Route path="/delivery-notes" element={<DeliveryNoteListPage />} />
            <Route path="/delivery-notes/new" element={<DeliveryNoteFormPage />} />
            <Route path="/delivery-notes/:id" element={<DeliveryNoteDetailPage />} />

            {/* Vehicle routes (Epic 34: Járműnyilvántartás) */}
            <Route path="/vehicles/rental" element={<RentalVehicleListPage />} />
            <Route path="/vehicles/rental/new" element={<RentalVehicleFormPage />} />
            <Route path="/vehicles/rental/:id" element={<RentalVehicleDetailPage />} />
            <Route path="/vehicles/rental/:id/edit" element={<RentalVehicleFormPage />} />
            <Route path="/vehicles/company" element={<CompanyVehicleListPage />} />
            <Route path="/vehicles/company/new" element={<CompanyVehicleFormPage />} />
            <Route path="/vehicles/company/:id" element={<CompanyVehicleDetailPage />} />
            <Route path="/vehicles/company/:id/edit" element={<CompanyVehicleFormPage />} />
            <Route path="/vehicles/expiring" element={<ExpiringDocumentsPage />} />

            {/* User routes */}
            <Route path="/users" element={<UserListPage />} />
            <Route path="/users/new" element={<UserFormPage />} />
            <Route path="/users/roles" element={<RoleManagementPage />} />
            <Route path="/users/:id" element={<UserDetailPage />} />
            <Route path="/users/:id/edit" element={<UserFormPage />} />

            {/* Messaging routes (Epic 67) */}
            <Route path="/messaging/sms" element={<SmsDashboardPage />} />
            <Route path="/messaging/sms/inbound" element={<InboundSmsPage />} />
            <Route path="/messaging/bulk-email" element={<BulkEmailPage />} />

            {/* Settings routes */}
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/settings/business-hours" element={<BusinessHoursPage />} />
            <Route path="/settings/email-templates" element={<EmailTemplateListPage />} />
            <Route path="/settings/email-smtp" element={<EmailSmtpSettingsPage />} />
            <Route path="/settings/email-logs" element={<EmailLogPage />} />
            <Route path="/settings/sms" element={<SmsSettingsPage />} />
            <Route path="/settings/notifications" element={<NotificationPreferencesPage />} />
            <Route path="/settings/ocr" element={<OcrSettingsPage />} />
            {/* kör-69 #19 (2a): mindkét kategória-route azonos guard alatt — az
                óradíj-konfig money-beállítás, a BE-n a HOURLY_RATE_* út is
                BOLTVEZETO. Két oldal két láthatósági szabállyal rosszabb, mint
                egy guard két route-on (🚦 ruling 2026-09-07). A guard UI-
                láthatóság; a valódi kontroll a BE @Roles marad. */}
            <Route
              path="/settings/hourly-rates"
              element={
                <RequireDashboardRole allowedRoles={CATEGORY_ADMIN_ROLES}>
                  <CategoryHourlyRateConfigPage />
                </RequireDashboardRole>
              }
            />
            <Route
              path="/settings/machine-categories"
              element={
                <RequireDashboardRole allowedRoles={CATEGORY_ADMIN_ROLES}>
                  <MachineCategoryAdminPage />
                </RequireDashboardRole>
              }
            />
            <Route path="/settings/supplier-settlement" element={<SupplierSettlementPage />} />
            <Route path="/settings/dashboard/permissions" element={<WidgetPermissionsPage />} />

            {/* Reports route */}
            <Route path="/reports" element={<ReportsPage />} />

            {/* Tasks route */}
            <Route path="/tasks" element={<TasksPage />} />

            {/* Chat route */}
            <Route path="/chat" element={<ChatPage />} />

            {/* Tenant Admin route */}
            <Route path="/tenant" element={<TenantSettingsPage />} />

            {/* Feature Flags route */}
            <Route path="/feature-flags" element={<FeatureFlagsPage />} />

            {/* Integration routes */}
            <Route path="/integrations/crm" element={<TwentyCRMPage />} />
            <Route path="/integrations/flow" element={<FlowPage />} />

            {/* rPI Porter prototype (ADR-102 v0.3 Option X visual validation) */}
            <Route path="/porter/inbox" element={<InboxListPage />} />
            <Route path="/porter/inbox/prototype" element={<InboxThreadDetailPage />} />
            <Route path="/porter/inbox/:threadId" element={<InboxThreadDetailPage />} />

            {/* rPI Porter settings (E-102 P2-1) — channels read-only skeleton */}
            <Route path="/porter/settings/channels" element={<SettingsChannelsPage />} />

            {/* rPI Porter settings (E-102 P3-1) — auto-reply rule editor */}
            <Route path="/porter/settings/auto-reply" element={<SettingsAutoReplyPage />} />

            {/* rPI Porter Partner Timeline (E-102 P3-3) — all threads for a partner */}
            <Route path="/porter/inbox/partners/:id/timeline" element={<PartnerTimelinePage />} />

            {/* Cash Management routes (Epic 62, Story 62-12) */}
            <Route path="/finance/cash" element={<KasszaPage />} />
            <Route path="/finance/cash/close" element={<VakzarasPage />} />
            <Route path="/finance/cash/szh" element={<SzhMappaPage />} />
            <Route path="/finance/cash/vouchers" element={<ExpenseVoucherPage />} />

            {/* General Ledger / Journal route (19-kör FE-1) */}
            <Route path="/finance/journal" element={<JournalPage />} />

            {/* Monthly Close route */}
            <Route path="/finance/monthly-close" element={<MonthlyClosePage />} />

            {/* TAM Tax routes (Epic 71) */}
            <Route path="/finance/tam" element={<TamDeclarationsPage />} />

            {/* Receivables Aging route (Epic 41, Story 41-1) */}
            <Route path="/finance/receivables/aging" element={<ReceivablesPage />} />

            {/* Bank-import route (35-kör Fázis-1 — Pénzügy bank-import) */}
            <Route path="/finance/bank-import" element={<BankImportListPage />} />

            {/* Fixed Asset routes (Epic 66) */}
            <Route path="/finance/fixed-assets" element={<FixedAssetListPage />} />
            <Route path="/finance/fixed-assets/:id" element={<FixedAssetDetailPage />} />

            {/* HR routes (Epic 111) */}
            <Route path="/hr/employees" element={<EmployeeListPage />} />
            <Route path="/hr/employees/:id" element={<EmployeeDetailPage />} />
            <Route path="/hr/attendance" element={<AttendancePage />} />
            <Route path="/hr/leave/request" element={<LeaveRequestPage />} />
            <Route path="/hr/leave/calendar" element={<LeaveCalendarPage />} />

            {/* Payroll routes (Epic 112 — PAY-7/PAY-10/PAY-11/PAY-12) */}
            <Route path="/payroll" element={<PayrollDashboardPage />} />
            <Route path="/payroll/config" element={<PayrollConfigPage />} />
            <Route path="/payroll/runs" element={<PayrollRunListPage />} />
            <Route path="/payroll/runs/new" element={<PayrollRunNewPage />} />
            <Route path="/payroll/runs/:id" element={<PayrollRunDetailPage />} />
            <Route path="/payroll/my-payslips" element={<PayslipPage />} />

            {/* MyForgeOS route */}
            <Route path="/myforgeos" element={<MyForgeOSPage />} />
          </Route>

          {/* Dev-only: JWT generator + tenant switcher (Story 107-9)
              Tree-shaken in production builds — import.meta.env.DEV = false at build time */}
          {import.meta.env.DEV && (
            <Route
              path="/dev/tenant-switcher"
              element={
                <Suspense fallback={<div>Loading...</div>}>
                  <DevTenantSwitcherPageLazy />
                </Suspense>
              }
            />
          )}

          {/* Dev-only: E-105 Phase 2 package UI komponensek vizuális preview */}
          {import.meta.env.DEV && (
            <Route
              path="/dev/package-preview"
              element={
                <Suspense fallback={<div>Loading...</div>}>
                  <DevPackagePreviewPageLazy />
                </Suspense>
              }
            />
          )}

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </QueryClientProvider>
  );
}

export default App;
